import {
  DsBadge,
  DsButton,
  DsDateTimePicker,
  type DsDateTimePickerProps,
  DsIconButton,
  DsProgressBar,
  DsSelect,
  DsSelectItem,
} from '@frontends/ds';
import { DsIcon } from '@frontends/ds-icons';
import { FloatingPanel } from '@frontends/ui-kit';
import { dateValueToSec, secToDateValue } from '@frontends/util-utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { selectAccount } from '../../features/initialSlice/initialSlice';
import {
  changeCurrentDate,
  changeMaxDate,
  resetSession,
  selectBalance,
  selectChartRes,
  selectChartSpeed,
  selectCurrentDate,
  selectInitialDate,
  selectIsFinishReached,
  selectIsPanelBlocked,
  selectIsPaused,
  selectIsReplayStarted,
  selectMaxDate,
  selectStepRes,
  setBalance,
  setChartSpeed,
  setInitialDate,
  setIsFinishReached,
  setIsPaused,
  setIsReplayStarted,
  setPanelBlocked,
  setStepRes,
  triggerDateChange,
} from '../../features/mainSlice/mainSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { DataFeed } from '../tradingView/lib/datafeed';
import { adjustBarTime, resolutionToSeconds } from '../tradingView/lib/helpers';
import { buildBalanceOptions, buildStepOptions, SPEED_OPTIONS } from './options';

type TBadgeStatus = 'idle' | 'live' | 'done';

export const ControlPanelWidget = (): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const config = useEntityConfig();
  const { t } = useTranslation();

  const BadgeStatusLabel: Record<TBadgeStatus, string> = {
    idle: t('badge.idle'),
    live: t('badge.live'),
    done: t('badge.done'),
  };
  const datafeed = useMemo(() => DataFeed.getInstance(config), [config]);

  const account = useAppSelector(selectAccount);
  const isPaused = useAppSelector(selectIsPaused);
  const currentDate = useAppSelector(selectCurrentDate);
  const initialDate = useAppSelector(selectInitialDate);
  const maxDate = useAppSelector(selectMaxDate);
  const chartRes = useAppSelector(selectChartRes);
  const stepRes = useAppSelector(selectStepRes);
  const chartSpeed = useAppSelector(selectChartSpeed);
  const balance = useAppSelector(selectBalance);
  const isPanelBlocked = useAppSelector(selectIsPanelBlocked);
  const isFinishReached = useAppSelector(selectIsFinishReached);
  const isReplayStarted = useAppSelector(selectIsReplayStarted);

  const badgeStatus: TBadgeStatus = isFinishReached ? 'done' : isPaused ? 'idle' : 'live';

  const progress = (() => {
    if (isFinishReached) return 100;

    if (!initialDate || !maxDate || maxDate <= initialDate) return 0;

    return Math.min(100, Math.max(0, ((currentDate - initialDate) / (maxDate - initialDate)) * 100));
  })();

  const minFromSec = useMemo(
    () =>
      config.VITE_REPLAY_MIN_DATE
        ? Math.floor(new Date(config.VITE_REPLAY_MIN_DATE + 'T00:00:00Z').getTime() / 1000)
        : 0,
    [config.VITE_REPLAY_MIN_DATE],
  );
  const maxToSec = Math.floor(Date.now() / 1000);

  const chartResSec = resolutionToSeconds[chartRes] ?? 60;
  const stepResSec = resolutionToSeconds[stepRes] ?? 60;

  const lastBarOfMaxTime = useMemo(
    () => adjustBarTime(maxDate * 1000, stepResSec, chartResSec) / 1000,
    [maxDate, stepResSec, chartResSec],
  );
  const maxFromSec = Math.min(maxToSec, lastBarOfMaxTime - 60);

  const minToSec = useMemo(
    () => adjustBarTime(initialDate * 1000, stepResSec, chartResSec) / 1000 + chartResSec,
    [initialDate, stepResSec, chartResSec],
  );

  const balanceOptions = useMemo(
    () => buildBalanceOptions(account?.balance, account?.currency),
    [account?.balance, account?.currency],
  );
  const stepOptions = useMemo(() => buildStepOptions(chartRes, datafeed), [chartRes, datafeed]);

  const stepDisabledKeys = stepOptions.filter((option) => option.disabled).map((option) => option.value);

  const isFromInvalid = currentDate < minFromSec || currentDate > maxFromSec;
  const isToInvalid = maxDate < minToSec || maxDate > maxToSec;

  const isPlayDisabled =
    isPanelBlocked ||
    !chartSpeed ||
    !stepRes ||
    !currentDate ||
    !maxDate ||
    currentDate >= maxDate ||
    isFromInvalid ||
    isToInvalid;
  const isSkipDisabled = currentDate >= maxDate || isFromInvalid || isToInvalid;

  const handleTogglePlay = (): void => {
    // if (isPaused) {
    //   if (currentDate >= maxDate) return;

    //   if (currentDate !== appliedFromSec || maxDate !== appliedToSec) {
    //     dispatch(setAppliedFromSec(currentDate));
    //     dispatch(setAppliedToSec(maxDate));
    //     dispatch(setRangeFromSec(currentDate));
    //     dispatch(setIsFinishReached(false));
    //     dispatch(triggerDateChange());
    //   }

    //   if (!rangeFromSec || isFinishReached) {
    //     dispatch(setRangeFromSec(currentDate));
    //   }
    //   dispatch(setIsFinishReached(false));
    // }
    dispatch(setIsReplayStarted(true));
    dispatch(setIsPaused(!isPaused));
  };

  const handleReset = (): void => {
    dispatch(resetSession());
  };

  const handleFromChange: DsDateTimePickerProps['onChange'] = (value) => {
    if (!value) return;

    let sec = dateValueToSec(value);

    if (sec >= lastBarOfMaxTime) {
      sec = maxFromSec;
    }

    if (sec < minFromSec) {
      sec = minFromSec;
    }

    dispatch(changeCurrentDate(sec));
    dispatch(setInitialDate(sec));
    dispatch(triggerDateChange());
  };

  const handleSkip = (): void => {
    dispatch(setIsFinishReached(false));
    dispatch(setStepRes(chartRes));
    dispatch(setChartSpeed(500));
    dispatch(setIsPaused(false));
    dispatch(setPanelBlocked(true));
  };

  return (
    <FloatingPanel
      size="sm"
      title={t('panel.title')}
      initialPosition="bottom-right"
      aria-label={t('panel.title')}
      iconElement={
        <span className="inline-flex items-center px-1.5 py-1 border border-brandPrimaryMuted rounded-md bg-brandPrimaryMuted text-textBrand">
          <DsIcon name="skip-forward" size="sm" aria-hidden="true" />
        </span>
      }
      headerExtra={
        <DsBadge
          variant={badgeStatus === 'live' ? 'success' : 'neutral'}
          size="sm"
          shape="square"
          showDot
          className={badgeStatus === 'live' ? 'motion-safe:animate-pulse' : undefined}
        >
          {BadgeStatusLabel[badgeStatus].toUpperCase()}
        </DsBadge>
      }
      headerExtraCollapsed={
        <DsIconButton
          size="sm"
          name={isPaused ? 'play-filled' : 'pause'}
          isDisabled={isPaused ? isPlayDisabled : isPanelBlocked}
          aria-pressed={!isPaused}
          aria-label={isPaused ? t('controls.play') : t('controls.pause')}
          onPress={handleTogglePlay}
        />
      }
      headerBottomCollapsed={
        <DsProgressBar value={progress} size="sm" aria-label={t('progress.replayProgress')} className="w-full" />
      }
    >
      <div className="grid grid-cols-2 gap-2 px-3 py-3">
        <div className="flex flex-col gap-1">
          <DsSelect
            isFullWidth
            label={t('fields.speed')}
            value={String(chartSpeed)}
            onChange={(key) => {
              if (key !== null) {
                dispatch(setChartSpeed(Number(key)));
              }
            }}
            disabledKeys={['500']}
            isDisabled={isPanelBlocked}
          >
            {SPEED_OPTIONS.filter((option) => option.value !== 500 || chartSpeed === 500).map((option) => (
              <DsSelectItem key={String(option.value)} id={String(option.value)} textValue={option.label}>
                {option.label}
              </DsSelectItem>
            ))}
          </DsSelect>
        </div>

        <div className="flex flex-col gap-1">
          <DsSelect
            isFullWidth
            label={t('fields.step')}
            value={stepRes}
            onChange={(key) => {
              if (key !== null) {
                dispatch(setStepRes(String(key)));
              }
            }}
            disabledKeys={stepDisabledKeys}
            isDisabled={isPanelBlocked}
          >
            {stepOptions.map((option) => (
              <DsSelectItem key={option.value} id={option.value} textValue={option.label}>
                {option.label}
              </DsSelectItem>
            ))}
          </DsSelect>
        </div>

        <DsDateTimePicker
          isFullWidth
          variant="segments"
          showFooter={false}
          label={t('fields.from')}
          aria-label={t('fields.from')}
          value={secToDateValue(initialDate)}
          minValue={secToDateValue(minFromSec)}
          maxValue={secToDateValue(maxFromSec)}
          onChange={handleFromChange}
          isDisabled={isPanelBlocked || isReplayStarted}
        />

        <DsDateTimePicker
          isFullWidth
          variant="segments"
          showFooter={false}
          label={t('fields.to')}
          aria-label={t('fields.to')}
          value={secToDateValue(maxDate)}
          minValue={secToDateValue(minToSec)}
          maxValue={secToDateValue(maxToSec)}
          onChange={(value) => {
            if (value) dispatch(changeMaxDate(dateValueToSec(value)));
          }}
          isDisabled={isPanelBlocked || isReplayStarted}
        />

        <div className="col-span-2">
          <DsSelect
            isFullWidth
            label={t('fields.balance')}
            value={String(balance)}
            onChange={(key) => {
              if (key !== null) {
                dispatch(setBalance(Number(key)));
              }
            }}
            isDisabled={isPanelBlocked || isReplayStarted}
          >
            {balanceOptions.map((option) => (
              <DsSelectItem key={String(option.value)} id={String(option.value)} textValue={option.label}>
                {option.label}
              </DsSelectItem>
            ))}
          </DsSelect>
        </div>
      </div>

      <div className="p-3 pt-3 border-t border-borderSecondary">
        <DsProgressBar label={t('progress.label')} value={progress} size="sm" className="mb-4 w-full" />

        <div className="flex justify-between gap-2">
          <DsButton
            variant="primary"
            size="md"
            className="flex-1"
            iconStart={isPaused ? 'play' : 'pause'}
            onPress={handleTogglePlay}
            isDisabled={isPaused ? isPlayDisabled : isPanelBlocked}
            aria-pressed={!isPaused}
          >
            {isPaused ? t('controls.play') : t('controls.pause')}
          </DsButton>

          <DsButton
            variant="secondary"
            size="md"
            className="flex-1"
            iconStart="skip-forward"
            onPress={handleSkip}
            isDisabled={isPanelBlocked || isSkipDisabled}
          >
            {t('controls.skip')}
          </DsButton>

          <DsButton
            variant="soft"
            size="md"
            className="flex-1"
            iconStart="history"
            onPress={handleReset}
            isDisabled={isPanelBlocked || !isPaused}
          >
            {t('controls.reset')}
          </DsButton>
        </div>
      </div>
    </FloatingPanel>
  );
};
